import type { ComponentChildren, VNode } from 'preact';
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { loadItem, loadMember, type Band, type Column, type Grid as GridDoc, type ItemDoc, type Mark, type MemberDoc, type Row } from '../data';
import { afterElection, CONTACT_FORMER, fmtLong, fmtShort, isTrialVote, leaderShort, leaderWords, ordinal, outcomeWords, pinnedLeader, TRIAL_NOTE } from '../format';
import { cls, officeDay, passedHow, rowCompare, trialWord, type PinnedBand, type SortBy } from '../grid/Grid';
import { GLYPH, JARGON, NO_ROLL_CALL, NO_ROLL_CALL_ANY } from '../grid/marks';
import { PHONE_TOP } from '../grid/PhoneGrid';
import { navigate, savedScroll, saveScroll, stepTo } from '../router';
import { useSwipe } from '../swipe';
import { Footer, NON_STATES } from './Chrome';
import { ItemMore, ItemSummaries, kindLabel, num } from './ItemModal';
import { history, placeName, wholeYears, yrs } from './MemberCard';
import { ShareButton, SharePanel, itemShare, memberShare, pageTitle } from './Share';

// THE PHONE'S ITEM AND MEMBER PAGES (Design's phone handoff, design_handoff_phone_view/README.md, "Item
// page" and "Member page"; step 3 of Dan's three). An item is Direction B's page: its results, then its
// summary (Dan, 29 Sep 2026: "Summary first"; the design had everyone's marks first, about 9,000px of
// House above the summary), then everyone's mark as three-across tiles, then the rest of the item. A
// member is Direction C's page: the facts, and the votes by month, what never reached the member
// folded. Both are screens over the grid, which stays where it was under them; the bottom bar steps to
// the next item or person, replacing the history entry, and Back returns to the same place on the page
// it left (the page's scroll is kept in its entry). Desktops keep ItemModal and MemberCard.

type Load<T> = { state: 'loading' } | { state: 'failed'; why: string } | { state: 'loaded'; doc: T };

const BAND_NAME: Record<Band, string> = { executive: 'White House', senate: 'Senate', house: 'House' };
const CHAMBER: Record<Band, string> = { executive: 'the President', senate: 'the Senate', house: 'the House' };
// the tiles' short words for a post, with the seat where it fits: a tile's line holds about fourteen
// capitals, so the Majority Leader's tile names the post alone (the page names the seat)
const TILE_POST: Record<string, (seat: string) => string> = {
  speaker: (seat) => `Speaker · ${seat}`, majority_leader: () => 'Maj. Leader',
};
const MONTH = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** Every row of the grid in the order it draws them: each band's pinned leader, its members by time in
 *  office or by name, then those who left this term (Grid.tsx layoutLines, with nothing filtered). */
export function displayOrder(grid: GridDoc, by: SortBy = 'tenure'): number[] {
  const out: number[] = [];
  const order = rowCompare(grid, by);
  for (const band of ['executive', 'senate', 'house'] as Band[]) {
    const serving: number[] = [], left: number[] = [];
    grid.rows.forEach((row, r) => { if (row.band === band) (row.left ? left : serving).push(r); });
    out.push(...serving.sort(order), ...left.sort(order));
  }
  return out;
}

/** The bands a screen can jump to (Dan, 29 Sep 2026: "Yes, add it"), with the grid's counts. */
export function bandList(grid: GridDoc, pinned: PinnedBand | null): { key: string; label: string; count: string }[] {
  const out = pinned && pinned.rows.length ? [{ key: 'pinned', label: 'Your representatives', count: pinned.count }] : [];
  for (const band of ['executive', 'senate', 'house'] as Band[]) {
    out.push({ key: band, label: BAND_NAME[band], count: String(grid.rows.filter((r) => r.band === band && !r.left).length) });
  }
  return out;
}

/** Move a phone page, drawn whole for that moment: what is off screen is otherwise held at a guessed
 *  height (phone.css, content-visibility), and a place measured against a guess is not the place. */
function goTo(page: HTMLElement, where: () => number) {
  page.classList.add('is-measuring');
  page.scrollTop = where();
  requestAnimationFrame(() => page.classList.remove('is-measuring'));
}
const placeIn = (page: HTMLElement, a: HTMLElement) => a.getBoundingClientRect().top - page.getBoundingClientRect().top + page.scrollTop;

/** Scroll the screen on top (a phone page, or the grid under the sticky head) to a band's start. */
export function jumpToBand(key: string) {
  const page = document.querySelector<HTMLElement>('.sr-ppage');
  const a = (page ?? document).querySelector<HTMLElement>(`[data-band-anchor="${key}"]`);
  if (!a) return;
  if (page) goTo(page, () => placeIn(page, a));
  else window.scrollTo({ top: a.getBoundingClientRect().top + window.scrollY - PHONE_TOP });
}

/** A member page's month, by its anchor (the month's label sticks, so its own place moves). */
export function jumpToMonthOf(ym: string) {
  const page = document.querySelector<HTMLElement>('.sr-ppage');
  const a = page?.querySelector<HTMLElement>(`[data-month="${ym}"]`);
  if (page && a) goTo(page, () => placeIn(page, a));
}

/** What a mark says, for a person on an item: the key's words, the way a chamber passed it without a
 *  roll call, and the President's two states the key's word leaves open. */
function markWords(grid: GridDoc, col: Column, row: Row, m: Mark): string {
  if (m === '.' && row.role === 'president') return 'Awaiting the President’s action';
  if (m === 'x' && row.role === 'president' && col.president === 'not_yet') return 'Not before the President yet';
  return trialWord(col, row.role, m) ?? passedHow(col, row.role, m) ?? grid.legend[m] ?? m;
}

/** False on a page's first drawing, true once that has reached the screen: what is below the first
 *  screen (an item's tiles, most of a member's months) waits for it, so the page shows at once. A
 *  mid-range phone (11x slowed here) took 1.0s to open Grassley's 311 rows drawn in one go. */
function useAfterFirstPaint(): boolean {
  const [done, setDone] = useState(false);
  useEffect(() => { const t = setTimeout(() => setDone(true), 0); return () => clearTimeout(t); }, []);
  return done;
}
const FIRST_ROWS = 30;         // a member's rows drawn at first: more than a screen holds

// ---- the page around both ----------------------------------------------------------------------

function PageShell({ pageKey, ready, label, onClose, onStep, bar, updated, children }: {
  pageKey: string; ready: boolean; label: string; onClose: () => void; onStep: (dir: 1 | -1) => void;
  bar: VNode | null; updated: string | null; children: ComponentChildren;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    ref.current?.focus({ preventScroll: true });
    // Escape goes back, unless a sheet is open over the page: the sheet takes it
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape' && !document.querySelector('.sr-layer')) close.current(); };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, []);
  // Where Back left it, once what is above has loaded; a new item or person starts at its top.
  useLayoutEffect(() => {
    const page = ref.current, y = savedScroll();
    if (ready && page) { if (y > 0) goTo(page, () => y); else page.scrollTop = 0; }
  }, [pageKey, ready]);
  useSwipe(ref, onStep);
  return (
    // any tap inside first keeps how far down the page is, in its own history entry
    <div ref={ref} class="sr-ppage" tabIndex={-1} onClickCapture={() => ref.current && saveScroll(ref.current.scrollTop)}>
      <div class="sr-pback-row"><button class="sr-pback" onClick={onClose}>← {label}</button></div>
      {children}
      <Footer updated={updated} page="page" phone madison />
      {bar}
    </div>
  );
}

// ---- the item ------------------------------------------------------------------------------------

const PRESIDENT: Record<string, string> = {
  signed: 'Signed', vetoed: 'Vetoed', without_signature: 'Law without signature', pending: 'Pending',
  not_yet: 'Not before the President yet', na: 'Not applicable', unknown: 'Became law; the record names no signature or veto',
};

/** Where it stands: each chamber's result and tally, and the President's. */
function Stands({ doc }: { doc: ItemDoc }) {
  const chamber = (ch: 'house' | 'senate') => {
    const c = doc.chambers[ch], name = ch === 'house' ? 'House' : 'Senate';
    let result = <>{c.status === 'pending' ? 'Pending' : 'Not applicable'}</>;
    let v: { yea: number | null; nay: number | null; present: number | null; not_voting: number | null } | null = null;
    if (c.status === 'roll_call' && c.vote) { result = <>{c.vote.result}</>; v = c.vote; }
    else if (c.status === 'voice' || c.status === 'roll_call') {
      const how = (c.how && NO_ROLL_CALL[c.how]) || NO_ROLL_CALL_ANY;
      result = <span class="sr-dotted" data-def={how.def}>{how.short}</span>;
    }
    const p = c.override ? c.passage : null;
    return [
      p && (
        <tr key={ch + '-passage'}>
          <th scope="row">{name} · passage</th>
          <td><span class="sr-st-result">{p.result ?? ((p.how && NO_ROLL_CALL[p.how]) || NO_ROLL_CALL_ANY).short}</span>
            {p.date && <span class="sr-st-date">{fmtLong(p.date)}</span>}</td>
          {p.result == null                       // no roll call: no tally, as a voice-vote row has none
            ? <><td></td><td></td><td></td><td></td></>
            : <><td>{num(p.yea)}</td><td>{num(p.nay)}</td><td>{num(p.present)}</td><td>{num(p.not_voting)}</td></>}
        </tr>
      ),
      <tr key={ch}>
        <th scope="row">{c.override ? `${name} · veto override` : isTrialVote(c.vote?.question) ? `${name} · trial verdict` : name}</th>
        <td><span class="sr-st-result">{result}</span>{c.date && <span class="sr-st-date">{fmtLong(c.date)}</span>}</td>
        <td>{v ? num(v.yea) : ''}</td><td>{v ? num(v.nay) : ''}</td><td>{v ? num(v.present) : ''}</td><td>{v ? num(v.not_voting) : ''}</td>
      </tr>,
    ];
  };
  const p = doc.president;
  return (
    <>
      <table class="sr-stands">
        <thead>
          <tr><th scope="col">Where</th><th scope="col">Result</th><th scope="col">Yea</th><th scope="col">Nay</th>
            <th scope="col"><abbr title="Present">P</abbr></th><th scope="col"><abbr title="Not voting">NV</abbr></th></tr>
        </thead>
        <tbody>
          {chamber('house')}
          {chamber('senate')}
          <tr>
            <th scope="row">President</th>
            <td><span class="sr-st-result">{PRESIDENT[p.status] ?? p.status}</span>{p.date && <span class="sr-st-date">{fmtLong(p.date)}</span>}</td>
            <td /><td /><td /><td />
          </tr>
        </tbody>
      </table>
      {(['house', 'senate'] as const).map((ch) => {
        const c = doc.chambers[ch];
        // a chamber that passed it without a roll call: the record's own words, as it gives them
        return c.status === 'voice' && c.words
          ? <div key={ch} class="sr-st-line">{ch === 'house' ? 'House' : 'Senate'}, in the record’s words: “{c.words}”</div>
          : null;
      })}
      {p.status === 'vetoed' && doc.outcome === 'became_law' && <div class="sr-st-line">Congress overrode the veto.</div>}
      {(['house', 'senate'] as const).some((ch) => isTrialVote(doc.chambers[ch].vote?.question)) && (
        <div class="sr-st-line">{TRIAL_NOTE}</div>
      )}
      {doc.vice_president_tie_breaker && (
        <div class="sr-st-line">Vice President: voted {doc.vice_president_tie_breaker.replace(/^Tie-breaker:\s*/i, '')} to break a tie</div>
      )}
    </>
  );
}

/** A chamber whose every current member has the same mark, and that mark no vote: one line, the mark
 *  still shown (Design's departure 6). The way a chamber passed it without a roll call is the site's
 *  own wording (marks.ts NO_ROLL_CALL), and a bill is "passed", a resolution "agreed to". */
function collapsedWords(m: Mark, band: Band, col: Column, anyLeft: boolean): string {
  if (m === 'x') {
    if (band !== 'executive') return `Not applicable: this item does not go to ${CHAMBER[band]}.`;
    return col.president === 'not_yet' ? 'Not before the President yet: the item is still in Congress.'
      : 'Not applicable: this item does not go to the President.';
  }
  if (m === '.') {
    return band === 'executive' ? 'Pending: both chambers have passed it and the President has not yet acted.'
      : `Pending: ${CHAMBER[band]} has not voted on it yet.`;
  }
  if (m === '~') {
    const how = col.how?.[band === 'house' ? 'house' : 'senate'];
    const way = ((how && NO_ROLL_CALL[how]) || NO_ROLL_CALL_ANY).result.replace(/^Agreed to /, '');
    const verb = col.kind.startsWith('nomination') ? 'confirmed' : col.kind.endsWith('resolution') ? 'agreed to' : 'passed';
    return `${BAND_NAME[band] === 'House' ? 'The House' : 'The Senate'} ${verb} it ${way}, so the record shows no individual votes.`;
  }
  return 'Not in office on this item’s dates.' + (anyLeft ? ' Those who were are under “Left office this term”.' : '');
}

function Tile({ grid, col, ci, r, reps }: { grid: GridDoc; col: Column; ci: number; r: number; reps: boolean }) {
  const row = grid.rows[r], m = grid.cells[r][ci] as Mark;
  const post = row.leader && !row.leader.until && TILE_POST[row.leader.role];  // dated posts do not fit a tile
  const sub = row.left && row.left_on ? `Left ${fmtShort(row.left_on)}`
    : row.role === 'president' ? 'President' : row.role === 'vp' ? 'Vice President'
    : post ? post(row.seat ?? '') : `${row.party ?? ''} · ${row.seat ?? ''}`;
  const leader = !reps && pinnedLeader(row.leader, row.left);
  return (
    <button class={'sr-tile' + (leader ? ' is-leader' : '')} aria-label={`${row.full}: ${markWords(grid, col, row, m)}`}
            onClick={() => navigate('/member/' + row.id, reps ? { state: { reps: true } } : {})}>
      <span class={'sr-cell sr-pcell c-' + cls(m)} aria-hidden="true">{GLYPH[m]}</span>
      <span class="sr-tile-text">
        <span class="sr-tile-last">{row.last}</span>
        <span class="sr-tile-sub">{sub}</span>
      </span>
    </button>
  );
}

function Tiles({ grid, col, ci, rows, reps = false }: { grid: GridDoc; col: Column; ci: number; rows: number[]; reps?: boolean }) {
  // a band off screen is not drawn until it nears (content-visibility), its height held meanwhile
  return (
    <div class="sr-tiles" style={{ containIntrinsicSize: `auto ${Math.ceil(rows.length / 3) * 48}px` }}>
      {rows.map((r) => <Tile key={r} grid={grid} col={col} ci={ci} r={r} reps={reps} />)}
    </div>
  );
}

/** Everyone's mark on the item, band by band, in the grid's order and with its pins. */
function Marks({ grid, ci, pinned, onBand, sortBy }: {
  grid: GridDoc; ci: number; pinned: PinnedBand | null; onBand: () => void; sortBy: SortBy;
}) {
  const col = grid.columns[ci];
  const [leftOpen, setLeftOpen] = useState<Record<string, boolean>>({});
  const label = (key: string, name: string, count: string, isPinned = false) => [
    <div key={key + '-a'} class="sr-band-anchor" data-band-anchor={key} />,
    <div key={key} class={'sr-band sr-ib' + (isPinned ? ' is-pinned' : '')}>
      <button class="sr-band-label sr-band-btn" aria-haspopup="dialog" onClick={onBand}
              aria-label={`${name}, ${count}. Jump to another band`}>
        <span class="sr-band-name">{name}</span>
        <span class="sr-band-count">{count}</span>
      </button>
    </div>,
  ];
  const out: VNode[] = [];
  if (pinned && pinned.rows.length) {
    out.push(...label('pinned', 'Your representatives', pinned.count, true));
    out.push(<Tiles key="pinned-t" grid={grid} col={col} ci={ci} rows={pinned.rows} reps />);
  }
  for (const band of ['executive', 'senate', 'house'] as Band[]) {
    const all = displayOrder(grid, sortBy).filter((r) => grid.rows[r].band === band);
    const serving = all.filter((r) => !grid.rows[r].left), left = all.filter((r) => grid.rows[r].left);
    out.push(...label(band, BAND_NAME[band], String(serving.length)));
    const marks = new Set(serving.map((r) => grid.cells[r][ci]));
    const one = marks.size === 1 ? ([...marks][0] as Mark) : null;
    if (one && 'x.~o'.includes(one)) {
      out.push(
        <div key={band + '-c'} class="sr-ib-one">
          <span class={'sr-cell sr-pcell c-' + cls(one)} aria-hidden="true">{GLYPH[one]}</span>
          <span>{collapsedWords(one, band, col, left.length > 0)}</span>
        </div>,
      );
    } else {
      out.push(<Tiles key={band + '-t'} grid={grid} col={col} ci={ci} rows={serving} />);
    }
    if (left.length) {
      const open = !!leftOpen[band];
      out.push(
        <button key={band + '-l'} class="sr-ptoggle" aria-expanded={open} onClick={() => setLeftOpen((s) => ({ ...s, [band]: !open }))}>
          <span class="sr-toggle-name">Left office this term</span>
          <span class="sr-toggle-hint">{left.length} {open ? '▾' : '▸'}</span>
        </button>,
      );
      if (open) out.push(<Tiles key={band + '-lt'} grid={grid} col={col} ci={ci} rows={left} />);
    }
    if (band === 'house' && grid.delegates.length) out.push(<Delegates key="dl" grid={grid} />);
  }
  return <div class="sr-marks">{out}</div>;
}

/** The brief: the House's non-voting members are named, not given a row (App.tsx DelegatesNote's words). */
function Delegates({ grid }: { grid: GridDoc }) {
  const ds = grid.delegates;
  return (
    <div class="sr-ib-delegates">
      Not in the grid: the members of the House for Washington, DC and the territories, who cannot vote on final
      passage.{' '}
      {ds.map((d, i) => (
        <span key={d.id}>
          {i === 0 ? '' : i === ds.length - 1 ? ' and ' : ', '}
          <button class="sr-link" onClick={() => navigate('/member/' + d.id)}>{d.full}</button> ({NON_STATES[d.seat.split('-')[0]] ?? d.seat})
        </span>
      ))}.
    </div>
  );
}

export function ItemPage({ id, grid, pinned, label, bar, updated, onClose, onStep, onBand, sortBy = 'tenure' }: {
  id: string; grid: GridDoc | null; pinned: PinnedBand | null; label: string; bar: VNode | null; updated: string | null;
  onClose: () => void; onStep: (dir: 1 | -1) => void; onBand: () => void; sortBy?: SortBy;
}) {
  const [load, setLoad] = useState<Load<ItemDoc>>({ state: 'loading' });
  const [procOpen, setProcOpen] = useState(false);
  const [titlesOpen, setTitlesOpen] = useState(false);
  const [share, setShare] = useState(false);
  useEffect(() => {
    setLoad({ state: 'loading' }); setProcOpen(false); setTitlesOpen(false); setShare(false);
    let live = true;
    loadItem(id).then((doc) => { if (!live) return; if (doc.id !== id) stepTo(`/item/${doc.id}`); setLoad({ state: 'loaded', doc }); })
      .catch((e) => live && setLoad({ state: 'failed', why: String(e.message ?? e) }));
    return () => { live = false; };
  }, [id]);
  useEffect(() => { if (load.state !== 'loading') pageTitle(load.state === 'loaded' ? itemShare(load.doc).title : null); }, [load]);
  const ci = grid ? grid.columns.findIndex((c) => c.id === id) : -1;
  const col = grid && ci >= 0 ? grid.columns[ci] : null;
  const doc = load.state === 'loaded' ? load.doc : null;
  const drawn = useAfterFirstPaint();
  // the heading comes from the grid at once, and from the item's file when the grid does not hold it
  const head = col ?? doc;
  const d = col?.dates;
  const dates = d ? [['House', d.house], ['Senate', d.senate], ['President', d.president]]
    : doc ? [['House', doc.chambers.house.date], ['Senate', doc.chambers.senate.date], ['President', doc.president.date]] : [];
  return (
    <PageShell pageKey={id} ready={load.state !== 'loading' && !!grid && drawn} label={label} onClose={onClose} onStep={onStep}
               bar={bar} updated={updated}>
      <div class="sr-ip-head">
        {head && (
          <>
            {/* a batch's number already says what it is: "Nominations en bloc (S.Res. 377)" */}
            <div class="sr-ip-kicker">
              <span class="sr-ip-num">{head.number}</span>{head.kind === 'nomination_batch' ? '' : ' · ' + kindLabel(head)}
            </div>
            <h1 class="sr-ip-title">{head.title || doc?.official_title || head.number}</h1>
            <div class="sr-ip-outcome">
              <span class={'sr-rule o-' + head.outcome} />
              <span>{outcomeWords(head.outcome, head.kind)}</span>
              {doc?.law_number && <span class="sr-ip-law">· {doc.law_number}</span>}
            </div>
            {dates.some(([, x]) => x) && (
              <div class="sr-ip-dates">{dates.filter(([, x]) => x).map(([w, x]) => `${w} ${fmtLong(x!)}`).join(' · ')}</div>
            )}
          </>
        )}
        {doc && <ShareButton open={share} onToggle={() => setShare(!share)} controls="sr-share-item" />}
        {doc && share && <SharePanel id="sr-share-item" what={itemShare(doc)} />}
        {doc && <Stands doc={doc} />}
        {load.state === 'loading' && <div class="sr-ip-state">Loading the item…</div>}
        {load.state === 'failed' && <div class="sr-ip-state">This item could not be loaded (<code>{load.why}</code>).</div>}
      </div>
      {doc && <ItemSummaries doc={doc} />}
      {grid && ci >= 0 && drawn && <Marks grid={grid} ci={ci} pinned={pinned} onBand={onBand} sortBy={sortBy} />}
      {doc && (
        <div class="sr-ip-more">
          <ItemMore doc={doc} procOpen={procOpen} setProcOpen={setProcOpen} titlesOpen={titlesOpen} setTitlesOpen={setTitlesOpen} />
        </div>
      )}
    </PageShell>
  );
}

// ---- the member ----------------------------------------------------------------------------------

export type MemberVote = { ci: number; mark: Mark; role: Row['role']; day: string | null; fold: string | null };

/** Why an item is folded on a member page: it never reached this person's chamber or office
 *  (Not applicable), has not yet (Pending), or came while they were not in office. The President's
 *  hatched cell is two things (T11), so it folds as two. */
function foldOf(m: Mark, role: Row['role'], col: Column): string | null {
  if (m === 'o') return 'o';
  const who = role === 'president' ? 'pres' : role === 'vp' ? 'vp' : role === 'sen' ? 'senate' : 'house';
  if (m === 'x') return role === 'president' && col.president === 'not_yet' ? 'x-notyet' : 'x-' + who;
  if (m === '.') return '.-' + who;
  return null;
}

function foldWords(key: string, n: number): string {
  const they = n === 1 ? 'it does' : 'they do', them = n === 1 ? 'it' : 'them';
  const place: Record<string, string> = { house: 'the House', senate: 'the Senate', pres: 'the President' };
  const [m, who] = key.split('-');
  if (key === 'o') return `${n} while not in office`;
  if (key === 'x-notyet') return `${n} not before the President yet: still in Congress`;
  if (key === 'x-vp') return `${n} not applicable: the Vice President votes only to break a tie`;
  if (m === 'x') return `${n} not applicable: ${they} not go to ${place[who]}`;
  if (who === 'pres') return `${n} pending: both chambers have passed ${them} and the President has not yet acted`;
  return `${n} pending: ${place[who] ?? 'the chamber'} has not acted yet`;
}

/** A person's items this term, by the month of their own chamber's or office's day, newest first.
 *  One person can hold two rows in a Congress (Vance: Senate, then Vice President); each item takes
 *  the row with a real mark, else the office it was waiting on, else "not in office". */
export function memberMonths(grid: GridDoc, id: string): { ym: string; votes: MemberVote[] }[] {
  const rows = grid.rows.map((r, i) => (r.id === id ? i : -1)).filter((i) => i >= 0);
  if (!rows.length) return [];
  const groups = new Map<string, MemberVote[]>();
  grid.columns.forEach((col, ci) => {
    const marks = rows.map((r) => grid.cells[r][ci] as Mark);
    let k = marks.findIndex((x) => !'xo.'.includes(x));
    if (k < 0) k = marks.findIndex((x) => x !== 'o');
    if (k < 0) k = 0;
    const mark = marks[k], role = grid.rows[rows[k]].role;
    const day = officeDay(col, role, mark);
    const ym = (day ?? col.date).slice(0, 7);
    if (!groups.has(ym)) groups.set(ym, []);
    groups.get(ym)!.push({ ci, mark, role, day, fold: foldOf(mark, role, col) });
  });
  return [...groups.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1)).map(([ym, votes]) => ({
    ym, votes: votes.sort((a, b) => ((b.day ?? '') > (a.day ?? '') ? 1 : (b.day ?? '') < (a.day ?? '') ? -1 : a.ci - b.ci)),
  }));
}

function VoteRow({ grid, v, inFold = false }: { grid: GridDoc; v: MemberVote; inFold?: boolean }) {
  const col = grid.columns[v.ci];
  const words = v.mark === '.' && v.role === 'president' ? 'Awaiting the President’s action'
    : v.mark === 'x' && v.role === 'president' && col.president === 'not_yet' ? 'Not before the President yet'
    : trialWord(col, v.role, v.mark) ?? passedHow(col, v.role, v.mark) ?? grid.legend[v.mark] ?? v.mark;
  const day = v.day ?? col.date;
  return (
    <button class={'sr-mv' + (inFold ? ' sr-mv-in' : '')} onClick={() => navigate('/item/' + col.id)}
            aria-label={`${col.number}: ${words}. ${col.title ?? ''}`}>
      <span class={'sr-cell sr-pcell c-' + cls(v.mark)} aria-hidden="true">{GLYPH[v.mark]}</span>
      <span class="sr-mv-text">
        <span class="sr-mv-l1">
          <span class="sr-mv-num">{col.number}</span>
          {!inFold && <span class={'sr-rule o-' + col.outcome} />}
          {!inFold && <span class="sr-mv-out">{outcomeWords(col.outcome, col.kind)}</span>}
          <span class="sr-mv-date">{fmtShort(day)}</span>
        </span>
        <span class="sr-mv-title">{col.title || col.number}</span>
      </span>
    </button>
  );
}

function Votes({ grid, id, all }: { grid: GridDoc; id: string; all: boolean }) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  let months = memberMonths(grid, id);
  if (!all) {
    let n = 0;
    months = months.filter((mo) => { const take = n < FIRST_ROWS; n += mo.votes.length; return take; });
  }
  return (
    <>
      {months.map(({ ym, votes }) => {
        const folds = new Map<string, MemberVote[]>();
        for (const v of votes) if (v.fold) { if (!folds.has(v.fold)) folds.set(v.fold, []); folds.get(v.fold)!.push(v); }
        return (
          <section key={ym}>
            <div class="sr-band-anchor" data-month={ym} />
            <div class="sr-mv-month">
              <span class="sr-band-name">{MONTH[+ym.slice(5, 7) - 1]} {ym.slice(0, 4)}</span>
              <span class="sr-band-count">{votes.length} {votes.length === 1 ? 'item' : 'items'}</span>
            </div>
            {votes.filter((v) => !v.fold).map((v) => <VoteRow key={v.ci} grid={grid} v={v} />)}
            {[...folds.entries()].map(([key, vs]) => {
              const k = ym + key, isOpen = !!open[k];
              return [
                <button key={key} class="sr-mv-fold" aria-expanded={isOpen} onClick={() => setOpen((s) => ({ ...s, [k]: !isOpen }))}>
                  <span class={'sr-cell sr-pcell c-' + cls(vs[0].mark)} aria-hidden="true">{GLYPH[vs[0].mark]}</span>
                  <span class="sr-mv-fold-text">{foldWords(key, vs.length)}</span>
                  <span class="sr-mv-fold-hint">{isOpen ? '▾' : '▸'}</span>
                </button>,
                ...(isOpen ? vs.map((v) => <VoteRow key={key + v.ci} grid={grid} v={v} inFold />) : []),
              ];
            })}
          </section>
        );
      })}
    </>
  );
}

export function MemberPage({ id, grid, pinned, reps, label, bar, updated, onClose, onStep, onTab }: {
  id: string; grid: GridDoc; pinned: PinnedBand | null; reps: boolean; label: string; bar: VNode | null;
  updated: string | null; onClose: () => void; onStep: (dir: 1 | -1) => void; onTab: (id: string) => void;
}) {
  const [load, setLoad] = useState<Load<MemberDoc>>({ state: 'loading' });
  const [termsOpen, setTermsOpen] = useState(false);
  useEffect(() => {
    setLoad({ state: 'loading' }); setTermsOpen(false);
    let live = true;
    loadMember(id).then((doc) => live && setLoad({ state: 'loaded', doc }))
      .catch((e) => live && setLoad({ state: 'failed', why: String(e.message ?? e) }));
    return () => { live = false; };
  }, [id]);
  useEffect(() => { if (load.state !== 'loading') pageTitle(load.state === 'loaded' ? load.doc.full : null); }, [load]);
  const doc = load.state === 'loaded' ? load.doc : null;
  const drawn = useAfterFirstPaint();
  const rowIdx = grid.rows.map((r, i) => (r.id === id ? i : -1)).filter((i) => i >= 0);
  const row = rowIdx.length ? grid.rows[rowIdx[rowIdx.length - 1]] : null;
  const delegate = grid.delegates.find((x) => x.id === id);
  // "Your representatives" as tabs, when the page was opened from that band
  const tabs = reps && pinned ? [...new Set(pinned.rows.map((r) => grid.rows[r].id))].map((x) => grid.rows.find((r) => r.id === x)!) : null;
  return (
    <PageShell pageKey={id} ready={load.state !== 'loading' && drawn} label={label} onClose={onClose} onStep={onStep} bar={bar} updated={updated}>
      {tabs && (
        <div class="sr-mp-tabs" role="tablist" aria-label="Your representatives">
          {tabs.map((t) => (
            <button key={t.id} role="tab" aria-selected={t.id === id} class={'sr-mp-tab' + (t.id === id ? ' is-on' : '')}
                    onClick={() => onTab(t.id)}>{t.last}</button>
          ))}
        </div>
      )}
      <div class="sr-mp-head">
        {!doc && <h1 class="sr-mp-name">{row?.full ?? delegate?.full ?? ''}</h1>}
        {load.state === 'loading' && <div class="sr-ip-state">Loading the member…</div>}
        {load.state === 'failed' && <div class="sr-ip-state">This member could not be loaded (<code>{load.why}</code>).</div>}
        {doc && <MemberHead doc={doc} grid={grid} row={row} reps={!!tabs} termsOpen={termsOpen} setTermsOpen={setTermsOpen} />}
      </div>
      {rowIdx.length > 0 && (
        <div class="sr-mp-votes">
          <div class="sr-mp-votes-head"><h2>Votes this term</h2><span>newest first</span></div>
          <Votes grid={grid} id={id} all={drawn} />
        </div>
      )}
      <div class="sr-mp-foot">
        {delegate && (
          <p>The member of the House for {placeName(delegate.seat.split('-')[0]) ?? delegate.seat} cannot vote on final passage,
            so has no vote on any of this term’s {grid.columns.length} items and is not in the grid.</p>
        )}
        <p>Shareable link: votesonrecord.org/member/{id}</p>
      </div>
    </PageShell>
  );
}

function MemberHead({ doc, grid, row, reps, termsOpen, setTermsOpen }: {
  doc: MemberDoc; grid: GridDoc; row: Row | null; reps: boolean; termsOpen: boolean; setTermsOpen: (b: boolean) => void;
}) {
  const m = doc.memberships.filter((x) => x.congress === grid.congress).sort((a, b) => (a.from < b.from ? 1 : -1))[0];
  const [share, setShare] = useState(false);
  useEffect(() => setShare(false), [doc.id]);
  const sharing = (
    <>
      <ShareButton open={share} onToggle={() => setShare(!share)} controls="sr-share-member" />
      {share && <SharePanel id="sr-share-member" what={memberShare(doc, m)} />}
    </>
  );
  if (!m) {
    return (
      <>
        <h1 class="sr-mp-name">{doc.full}</h1>
        {sharing}
        <p class="sr-mp-note">Not a member in the {ordinal(grid.congress)} Congress. Served in:{' '}
          {doc.memberships.map((x) => ordinal(x.congress)).join(', ') || 'none published yet'}.</p>
      </>
    );
  }
  const exec = m.role === 'president' || m.role === 'vp';
  const chamber = m.role === 'sen' ? 'Senate' : m.role === 'rep' ? 'House' : 'White House';
  const post = leaderShort(m.leader);
  const kicker = reps ? `Your representatives · ${chamber}` : [chamber, post].filter(Boolean).join(' · ');
  const sub = [m.party, exec ? (m.role === 'president' ? 'President' : 'Vice President') : m.seat,
    m.leader && (m.leader.from || m.leader.until) ? leaderWords(m.leader) : null,
    m.left ? `Left office ${fmtLong(m.to)}` : null].filter(Boolean).join(' · ');
  const isSpeaker = grid.rows.some((r, i) => r.id === doc.id && grid.cells[i].includes('s'));
  const a = m.attendance;
  const noVote = m.role === 'president' ? 'The President does not vote on roll calls.'
    : m.role === 'vp' ? 'The Vice President votes only to break a tie in the Senate.' : null;
  const missed = a && a.eligible ? `${a.missed} of ${a.eligible} · ${((a.missed / a.eligible) * 100).toFixed(1)}%` : '—';
  const pct = Math.min(100, Math.max(2, (m.tenure_days / 365.2425 / grid.tenure_scale_years) * 100));
  const sinceFirst = doc.first_sworn ? wholeYears(doc.first_sworn, m.left ? m.to : grid.as_of) : null;
  const current = doc.terms.find((t) => t.start <= grid.as_of && grid.as_of < t.end);
  const terms = history(doc, grid.as_of);
  const tel = doc.contact.phone ? 'tel:' + doc.contact.phone.replace(/[^\d+]/g, '') : null;
  return (
    <>
      <div class="sr-mp-kicker">{kicker}</div>
      <h1 class="sr-mp-name">{doc.full}</h1>
      <div class="sr-mp-sub">{sub}</div>
      {sharing}
      <div class="sr-mp-facts">
        <div>
          <span class="sr-mp-k">Time in office</span>
          <span class="sr-mp-v">{m.years} {m.years === 1 ? 'year' : 'years'}</span>
          <span class="sr-mp-bar"><span style={{ width: pct + '%' }} /></span>
        </div>
        <div><span class="sr-mp-k">Roll calls missed</span><span class="sr-mp-v">{noVote ? '—' : missed}</span></div>
        {/* "Service began", not the design's "First sworn in": the date follows the Biographical
            Directory, which dates some service from a special election before any oath (21 Sep 2026). */}
        <div><span class="sr-mp-k">Service began</span><span class="sr-mp-v">{doc.first_sworn ? fmtLong(doc.first_sworn) : '—'}</span></div>
        {m.left
          ? <div><span class="sr-mp-k">Left office</span><span class="sr-mp-v">{fmtLong(m.to)}</span></div>
          : afterElection(m)
            ? <div><span class="sr-mp-k">{afterElection(m)![0]}</span><span class="sr-mp-v">{afterElection(m)![1]}</span></div>
            : <div><span class="sr-mp-k">{exec ? 'Next presidential election' : 'Next election'}</span><span class="sr-mp-v">{m.next_election ?? '—'}</span></div>}
        {!noVote && a && a.not_listed > 0 && (
          <div><span class="sr-mp-k">{isSpeaker ? 'Not listed (Speaker)' : 'Not listed'}</span><span class="sr-mp-v">{a.not_listed} roll calls</span></div>
        )}
      </div>
      <div class="sr-mp-notes">
        {row?.gap && <p>Service not continuous; gaps are not counted.</p>}
        {noVote && <p>{noVote}</p>}
        {!noVote && a && a.not_listed > 0 && <p>{isSpeaker ? JARGON.speaker : 'The official record does not list them on those roll calls.'}</p>}
        {!noVote && a && <p>Roll calls missed: all roll calls in the {ordinal(grid.congress)} Congress, not only final votes.</p>}
      </div>
      <button class="sr-mp-terms-btn" aria-expanded={termsOpen} onClick={() => setTermsOpen(!termsOpen)}>
        <span>Terms in office</span><span class="sr-mp-hint">{terms.length} {termsOpen ? '▾' : '▸'}</span>
      </button>
      {termsOpen && (
        <div class="sr-mp-terms">
          <div><span>Years actually served</span><span>{yrs(m.years)}</span></div>
          {sinceFirst != null && <div><span>Years since service began</span><span>{yrs(sinceFirst)}</span></div>}
          {!m.left && current && <div><span>Current term ends</span><span>{fmtLong(current.end)}</span></div>}
          {terms.map((h, i) => <div key={i}><span>{h.what}</span><span>{h.when}</span></div>)}
        </div>
      )}
      <div class="sr-mp-contact">
        {tel && <a href={tel}>{doc.contact.phone}</a>}
        {doc.contact.form && <a href={doc.contact.form} rel="noopener">Contact form</a>}
        {!tel && !doc.contact.form && (
          <p class="sr-mp-note">{doc.contact.former ? CONTACT_FORMER : 'No office contact in the record.'}</p>
        )}
      </div>
    </>
  );
}
